import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import clsx from "clsx";

import { makeStyles, type Theme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import Alert from "@material-ui/lab/Alert";

import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";
import LoadableButton from "src/components/LoadableButton";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { activeDialogPropsSelector } from "src/store/adminHtml/selector";
import NumberField from "src/components/NumberField";
import ConditionalTooltip from "src/components/ConditionalTooltip";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import { journalEntrySelector } from "src/store/transaction/selector";
import Autocomplete from "src/components/Autocomplete";
import {
  generalLedgerAccountTypeToLabel,
  GeneralLedgerAccountTypeOrderById,
} from "src/utils/general-ledger-accounts";
import { formatCurrencyValue } from "src/utils/formatter";
import {
  upsertJournalEntry,
  deleteJournalEntry,
  getJournalEntry,
} from "src/apiService/modules/transactions";

import ConfirmDialog from "./ConfirmDialog";
import { AddGeneralLedgerAccountDialog } from "src/pages/RootDialogs/AddGeneralLedgerAccountDialog";
import type { GeneralLedgerAccountV2 } from "src/interfaces/generalLedgerAccount.interface";

const useStyles = makeStyles((theme: Theme) => ({
  titleEdit: {
    "& > button": {
      margin: theme.spacing(-0.5, 0),
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cell: {
    padding: theme.spacing(0, 0.5),
  },
  narrow: {
    maxWidth: "6em",
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
}));

const DefaultRow = {
  key: "1",
  amount: "" as number | string,
  description: "",
  gl_account_id: undefined as string | undefined,
  account: undefined as string | undefined,
};
const InitialValues = {
  date: new Date(),
  rows: [
    { ...DefaultRow, key: 1 },
    { ...DefaultRow, key: 2 },
  ],
};

function Content({ id, onClose, data }) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [addGlAccountRowIndex, setAddGlAccountRowIndex] = useState<
    number | null
  >(null);
  const classes = useStyles();
  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: data || InitialValues,
    validate: (values) => {
      const errors: any = {};
      const addRowError = (i: number, key: string, text: string) => {
        if (!errors.rows) errors.rows = {};
        if (!errors.rows[i]) errors.rows[i] = {};
        errors.rows[i][key] = text;
      };

      let total = 0;
      for (let i = 0; i < values.rows.length; i++) {
        const row = values.rows[i];
        if (!row.gl_account_id || !row.account)
          addRowError(
            i,
            "gl_account_id",
            "A general ledger account must be selected"
          );
        if (!row.amount)
          addRowError(i, "amount", "Debit or Credit must be filled");
        total += +row.amount || 0;
      }

      if (Math.abs(total || 0).toFixed(2) !== "0.00") {
        if (!errors.rows) errors.rows = {};
        errors.rows.sum =
          "There must be no difference between debits and credits";
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        await upsertJournalEntry({
          id,
          date: values.date,
          rows: values.rows as any,
        });
        toast.success("Saved successfully!");
        onClose();
      } catch (e) {
        toast.error(`There was an error: ${(e as Error).toString()}`);
      }
    },
  });
  const generalLedgerAccounts = useSelector(generalLedgerAccountsSelector);
  const options = useMemo(() => {
    const accounts = [...generalLedgerAccounts].filter((e) => !!e);

    accounts.sort(
      (a, b) =>
        GeneralLedgerAccountTypeOrderById[a.type[0]] -
          GeneralLedgerAccountTypeOrderById[b.type[0]] ||
        a.name.localeCompare(b.name)
    );

    accounts.unshift({
      name: "Add New Account",
      type: ["new"],
      id: "add_new_account",
    } as any);

    return accounts.filter((a) => !a.deleted);
  }, [generalLedgerAccounts]);

  const debits = formik.values.rows.reduce(
    (sum, row) => sum + (row.amount > 0 ? +row.amount : 0),
    0
  );
  const credits = formik.values.rows.reduce(
    (sum, row) => sum + (row.amount < 0 ? -row.amount : 0),
    0
  );
  const shouldShowDelete = formik.values.rows.length > 2;

  const setRowGlAccount = (
    rowIndex: number,
    account: GeneralLedgerAccountV2
  ) => {
    const rows = [...formik.values.rows];
    rows[rowIndex] = {
      ...rows[rowIndex],
      gl_account_id: account.id,
      account: account.name,
    };
    formik.setFieldValue("rows", rows);
  };

  return (
    <>
      <AddGeneralLedgerAccountDialog
        open={addGlAccountRowIndex !== null}
        onClose={(account) => {
          const rowIndex = addGlAccountRowIndex;
          setAddGlAccountRowIndex(null);
          if (account && rowIndex !== null) {
            setRowGlAccount(rowIndex, account);
          }
        }}
      />
      <ConfirmDialog
        open={confirmDialogOpen}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={async () => {
          if (!id) return;

          await deleteJournalEntry(id);
          onClose();
        }}
      />
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        {id ? (
          <DialogTitle disableTypography className={classes.titleEdit}>
            <Typography variant="h6" component="h2">
              Edit Journal Entry
            </Typography>
            <IconButton
              onClick={() => {
                setConfirmDialogOpen(true);
              }}
              disabled={formik.isSubmitting}
              className={classes.deleteButton}
            >
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          </DialogTitle>
        ) : (
          <DialogTitle>Add Journal Entry</DialogTitle>
        )}

        <DialogContent>
          <ZonedKeyboardDatePicker
            inputProps={{
              name: "date",
              autoFocus: true,
              onFocus: (e) => e.target.select(),
            }}
            onBlur={formik.handleBlur}
            disableToolbar
            fullWidth
            variant="inline"
            format="MM/dd/yyyy"
            margin="dense"
            label="Date"
            required
            autoOk={true}
            value={formik.values.date || null}
            disabled={formik.isSubmitting}
            onChange={(d) => {
              if (!d) return;
              formik.setFieldValue("date", d);
            }}
            inputVariant="outlined"
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell id={"general-ledger-cell"}>
                    General Ledger Account
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formik.values.rows.map((row, i) => {
                  const setFieldValue = (...args) => {
                    const rows = [...formik.values.rows];
                    const row = {
                      ...rows[i],
                    };

                    for (let i = 0; i < args.length; i += 2) {
                      const key = args[i];
                      const value = args[i + 1];
                      row[key] = value;
                    }

                    rows[i] = row;
                    formik.setFieldValue("rows", rows);
                  };
                  const getErrorForField = (key) =>
                    formik.touched?.rows?.[i]?.[key] &&
                    formik.errors?.rows?.[i]?.[key];
                  return (
                    <TableRow key={row.key}>
                      <TableCell className={classes.cell}>
                        <Autocomplete
                          disabled={formik.isSubmitting}
                          options={options}
                          fullWidth
                          value={
                            (row.gl_account_id || row.account || null) as any
                          }
                          onBlur={formik.handleBlur}
                          onChange={(_e, value) => {
                            if (
                              value?.name === "Add New Account" ||
                              value?.id === "add_new_account"
                            ) {
                              setAddGlAccountRowIndex(i);
                              return;
                            }
                            setFieldValue(
                              "gl_account_id",
                              value?.id || "",
                              "account",
                              value?.name || ""
                            );
                          }}
                          getOptionLabel={(option) =>
                            option?.name ||
                            options.find((opt) => opt.id === option)?.name ||
                            options.find((opt) => opt.name === option)?.name
                          }
                          groupBy={(option) =>
                            option.type[0] === "new"
                              ? ""
                              : generalLedgerAccountTypeToLabel(option.type)[0]
                          }
                          getOptionSelected={(option, value) =>
                            option.id === value || option.name === value
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Account"
                              variant="outlined"
                              margin="dense"
                              size="small"
                              name={`rows[${i}].gl_account_id`}
                              error={!!getErrorForField("gl_account_id")}
                              helperText={getErrorForField("gl_account_id")}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <TextField
                          name={`rows[${i}].description`}
                          onBlur={formik.handleBlur}
                          disabled={formik.isSubmitting}
                          label="Description"
                          variant="outlined"
                          size="small"
                          margin="dense"
                          fullWidth
                          value={row.description || ""}
                          onChange={(ev) =>
                            setFieldValue("description", ev.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        className={clsx(classes.cell, classes.narrow)}
                      >
                        <ConditionalTooltip
                          title="Only one of the credit or debit fields can be filled"
                          wrap
                          condition={row.amount < 0}
                        >
                          <NumberField
                            name={`rows[${i}].amount`}
                            onBlur={formik.handleBlur}
                            disabled={formik.isSubmitting || row.amount < 0}
                            variant="outlined"
                            size="small"
                            margin="dense"
                            fullWidth
                            value={
                              row.amount < 0
                                ? ""
                                : row.amount === 0
                                ? ""
                                : row.amount
                            }
                            onChange={(ev) => {
                              setFieldValue("amount", ev.target.value);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                            error={!!getErrorForField("amount")}
                            helperText={getErrorForField("amount")}
                          />
                        </ConditionalTooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        className={clsx(classes.cell, classes.narrow)}
                      >
                        <ConditionalTooltip
                          title="Only one of the credit or debit fields can be filled"
                          wrap
                          condition={row.amount > 0}
                        >
                          <NumberField
                            name={`rows[${i}].amount`}
                            onBlur={formik.handleBlur}
                            disabled={formik.isSubmitting || row.amount > 0}
                            variant="outlined"
                            size="small"
                            margin="dense"
                            fullWidth
                            value={
                              row.amount > 0
                                ? ""
                                : row.amount === 0
                                ? ""
                                : row.amount
                                ? -row.amount
                                : row.amount
                            }
                            onChange={(ev) => {
                              const value = ev.target.value;
                              setFieldValue("amount", value ? -value : value);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                            error={!!getErrorForField("amount")}
                            helperText={getErrorForField("amount")}
                          />
                        </ConditionalTooltip>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {shouldShowDelete ? (
                          <IconButton
                            disabled={formik.isSubmitting}
                            className={classes.deleteButton}
                            onClick={() => {
                              if (formik.values.rows.length <= 2) return;
                              const rows = [...formik.values.rows];
                              rows.splice(i, 1);
                              formik.setFieldValue("rows", rows);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell align="center">
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        formik.setFieldValue("rows", [
                          ...formik.values.rows,
                          { ...DefaultRow, key: Date.now() },
                        ]);
                      }}
                    >
                      Add Line
                    </Button>
                  </TableCell>
                  <TableCell align="right">Total:</TableCell>
                  <TableCell className={classes.cell} align="right">
                    {formatCurrencyValue(debits)}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {formatCurrencyValue(credits)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell align="right">Difference:</TableCell>
                  <TableCell className={classes.cell} align="right">
                    {credits > debits
                      ? formatCurrencyValue(credits - debits)
                      : ""}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {debits > credits
                      ? formatCurrencyValue(debits - credits)
                      : ""}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {(formik.errors as any)?.rows?.sum ? (
            <Box mt={2}>
              <Alert severity="error">{(formik.errors.rows as any).sum}</Alert>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <LoadableButton
            type="submit"
            color="primary"
            variant="contained"
            loading={formik.isSubmitting}
          >
            {id ? "Update" : "Add"}
          </LoadableButton>
        </DialogActions>
      </form>
    </>
  );
}

function Wrapper({ onClose }) {
  const activeDialogProps = useSelector(activeDialogPropsSelector);
  const [id] = useState((activeDialogProps as any)?.id);
  const dataFromStore = useSelector((state) => journalEntrySelector(state, id));
  const [fallbackData, setFallbackData] = useState<typeof dataFromStore>();
  const [isLoadingFallback, setIsLoadingFallback] = useState(false);

  useEffect(() => {
    if (dataFromStore) setFallbackData(undefined);
  }, [dataFromStore]);

  useEffect(() => {
    let cancelled = false;

    async function loadFallback() {
      if (!id || dataFromStore) return;

      try {
        setIsLoadingFallback(true);
        const fallback = await getJournalEntry(id);
        if (!cancelled) setFallbackData(fallback as any);
      } catch (e) {
        if (!cancelled) {
          toast.error(
            `There was an error loading this journal entry: ${String(e)}`
          );
        }
      } finally {
        if (!cancelled) setIsLoadingFallback(false);
      }
    }

    loadFallback();
    return () => {
      cancelled = true;
    };
  }, [id, dataFromStore]);

  const data = dataFromStore || fallbackData;

  if (id && !data && isLoadingFallback) {
    return (
      <DialogContent>
        <Typography variant="body2">Loading journal entry...</Typography>
      </DialogContent>
    );
  }

  return <Content id={id} onClose={onClose} data={data} />;
}

function AddJournalDialog({ open }: { open: boolean }) {
  const dispatch = useDispatch();
  const handleClose = () => dispatch(setActiveDialog(""));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      fullWidth
      maxWidth="md"
      disableEnforceFocus
    >
      <Wrapper onClose={handleClose} />
    </Dialog>
  );
}

export default AddJournalDialog;
